import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface CategoryOption {
  readonly categoryCode: string;
  readonly categoryName: string;
}
const PRIORITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'DEFAULT'];

export function M24RuleBuilderPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [ruleCode, setRuleCode] = useState('');
  const [ruleName, setRuleName] = useState('');
  const [ruleCategory, setRuleCategory] = useState('');
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [priority, setPriority] = useState('MEDIUM');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: CategoryOption[] }>('/v1/rule-categories', { token })
      .then((r) => {
        if (!alive) return;
        setCategories(r.data);
        if (r.data.length > 0) setRuleCategory((prev) => prev || r.data[0].categoryCode);
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setSubmitting(true);
    apiRequest<{ data: { ruleCode: string; ruleName: string } }>('/v1/rules', {
      method: 'POST',
      token,
      body: {
        ruleCode,
        ruleName,
        ruleCategory,
        priority,
        description: description || undefined,
      },
    })
      .then((r) => { setMessage(t('m24.ruleBuilder.msgCreated', { rule: `${r.data.ruleCode} \u2014 ${r.data.ruleName}` })); setRuleCode(''); setRuleName(''); setDescription(''); })
      .catch((err) => setError((err as Error).message))
      .finally(() => setSubmitting(false));
  }

  return (
    <main aria-label="rule-builder">
      <h1>{t('m24.ruleBuilder.title')}</h1>
      <form aria-label="rule-form" onSubmit={onSubmit}>
        <label htmlFor="ruleCode">{t('m24.ruleBuilder.ruleCode')}</label>
        <input id="ruleCode" value={ruleCode} onChange={(e) => setRuleCode(e.target.value)} />
        <label htmlFor="ruleName">{t('m24.ruleBuilder.ruleName')}</label>
        <input id="ruleName" value={ruleName} onChange={(e) => setRuleName(e.target.value)} />
        <label htmlFor="ruleCategory">{t('m24.ruleBuilder.category')}</label>
        <select id="ruleCategory" value={ruleCategory} onChange={(e) => setRuleCategory(e.target.value)}>
          {categories.length === 0 && <option value="">{t('m24.ruleBuilder.noCategories')}</option>}
          {categories.map((c) => <option key={c.categoryCode} value={c.categoryCode}>{c.categoryName}</option>)}
        </select>
        <label htmlFor="priority">{t('m24.ruleBuilder.priority')}</label>
        <select id="priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <label htmlFor="description">{t('m24.ruleBuilder.description')}</label>
        <textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} />
        <button type="submit" disabled={submitting || !ruleName.trim() || !ruleCategory.trim()}>{t('m24.ruleBuilder.submit')}</button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
    </main>
  );
}
